import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/docs/plugins/")({
	component: PluginsOverviewPage,
});

function H({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<h2
			id={id}
			className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mt-14 mb-4 pt-6 border-t border-zinc-100 dark:border-zinc-800/50 scroll-mt-20 first:border-0 first:mt-0 first:pt-0"
		>
			{children}
		</h2>
	);
}

function Pre({ title, children }: { title?: string; children: string }) {
	return (
		<div className="rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden mb-4">
			{title && (
				<div className="px-3 py-1 text-[9px] text-zinc-500 uppercase tracking-widest border-b border-zinc-800 font-medium">
					{title}
				</div>
			)}
			<pre className="px-3 py-2 overflow-x-auto text-[11px] leading-relaxed text-zinc-300 font-mono">
				{children}
			</pre>
		</div>
	);
}

function DocTable({
	headers,
	children,
}: { headers: string[]; children: React.ReactNode }) {
	return (
		<div className="rounded border border-zinc-200 dark:border-zinc-800 mb-4 text-[12px]">
			<Table>
				<TableHeader>
					<TableRow className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
						{headers.map((h) => (
							<TableHead
								key={h}
								className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto"
							>
								{h}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
					{children}
				</TableBody>
			</Table>
		</div>
	);
}

function PluginsOverviewPage() {
	const { t } = useTranslation("docs");
	return (
		<div className="w-full">
			<div className="mb-4">
				<h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
					{t("plugins_title")}
				</h1>
				<p
					className="mt-1.5 text-[13px] text-zinc-500"
					dangerouslySetInnerHTML={{ __html: t("plugins_intro") }}
				/>
			</div>

			<H id="architecture">{t("plugins_architecture")}</H>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("plugins_arch_desc") }}
			/>
			<ul className="text-[12px] text-zinc-500 space-y-1.5 list-disc list-inside mb-4">
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("plugins_arch_sandbox")}
					</strong>{" "}
					— {t("plugins_arch_sandbox_desc")}
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("plugins_arch_capabilities")}
					</strong>{" "}
					— {t("plugins_arch_capabilities_desc")}
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("plugins_arch_multitenancy")}
					</strong>{" "}
					— {t("plugins_arch_multitenancy_desc")}
				</li>
			</ul>

			<H id="extension-points">{t("plugins_extension_points")}</H>
			<DocTable
				headers={[
					"Extension point",
					t("plugins_ext_io"),
					t("plugins_ext_purpose"),
				]}
			>
				<TableRow className="border-b border-zinc-100 dark:border-zinc-900/50 last:border-0">
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono">enrichment.extract</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						<code className="text-[11px] font-mono">
							EnrichmentInput → ExtractResult
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("plugins_ext_extract")}
					</TableCell>
				</TableRow>
				<TableRow className="border-b border-zinc-100 dark:border-zinc-900/50 last:border-0">
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono">enrichment.calculate</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						<code className="text-[11px] font-mono">
							EnrichmentInput → ExtractResult
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("plugins_ext_calculate")}
					</TableCell>
				</TableRow>
				<TableRow className="border-b border-zinc-100 dark:border-zinc-900/50 last:border-0">
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono">pipeline.step</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						<code className="text-[11px] font-mono">
							PipelineStepInput → PipelineStepOutput
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("plugins_ext_pipeline")}
					</TableCell>
				</TableRow>
				<TableRow className="border-b border-zinc-100 dark:border-zinc-900/50 last:border-0">
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono">event.handler</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						<code className="text-[11px] font-mono">
							EventHandlerInput → EventHandlerOutput
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("plugins_ext_event")}
					</TableCell>
				</TableRow>
			</DocTable>

			<H id="example">{t("plugins_example_title")}</H>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("plugins_example_desc")}
			</p>
			<Pre title="src/lib.rs">{`use plugin_sdk::prelude::*;

extraction_handler!(extract_metal_spec, |input: EnrichmentInput| {
    // "Арматура А500С Ø12 L=11700мм" → shape, dimensions, grade
    ExtractResult {
        confidence: 0.95,
        action: "extracted".into(),
        extracted: json!({
            "shape": {"value": "rebar"},
            "dimensions": {"value": {"diameter": 12.0}},
            "steel_grade": {"value": "A500C"},
        }),
        metadata: None,
    }
});

calculation_handler!(calculate_metal_weight, |input: EnrichmentInput| {
    // shape + dimensions → linear_density, weight_per_piece
    ExtractResult {
        confidence: 0.99,
        action: "extracted".into(),
        extracted: json!({
            "linear_density_kg_per_m": {"value": 0.888},
            "weight_per_piece_kg": {"value": 10.41},
        }),
        metadata: None,
    }
});`}</Pre>

			<H id="next">{t("plugins_next")}</H>
			<ul className="text-[12px] text-zinc-500 space-y-1.5 list-disc list-inside mb-4">
				<li>
					<Link
						to="/docs/plugins/getting-started"
						className="text-primary hover:underline"
					>
						{t("getting_started")}
					</Link>{" "}
					— {t("plugins_next_getting_started")}
				</li>
				<li>
					<Link
						to="/docs/plugins/sdk-reference"
						className="text-primary hover:underline"
					>
						{t("sdk_reference")}
					</Link>{" "}
					— {t("plugins_next_sdk")}
				</li>
				<li>
					<Link
						to="/docs/plugins/capabilities"
						className="text-primary hover:underline"
					>
						{t("capabilities")}
					</Link>{" "}
					— {t("plugins_next_capabilities")}
				</li>
				<li>
					<Link
						to="/docs/plugins/publishing"
						className="text-primary hover:underline"
					>
						{t("publishing")}
					</Link>{" "}
					— {t("plugins_next_publishing")}
				</li>
			</ul>

			<div className="mt-14 pt-4 border-t border-zinc-100 dark:border-zinc-800/50 text-center">
				<p className="text-[10px] text-zinc-400 tracking-wider uppercase">
					OmniOS Plugin System
				</p>
			</div>
		</div>
	);
}
